import { useState } from "react";
import { useQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";

import { DataBlock } from "@roqua/components/DataBlock";
import { Section } from "@roqua/components/sections";

import { SkeletonLoader } from "@/components/SkeletonLoader";
import { Filters } from "@/features/Questionnaires/Filters";
import { GetAllQuestionnaires } from "@/features/Questionnaires/graphql";
import { useDebouncedSearch, useQuestionnairePagination } from "@/features/Questionnaires/hooks";
import { Pagination } from "@/features/Questionnaires/Pagination";
import { QuestionnaireItemList } from "@/features/Questionnaires/QuestionnaireItem";
import { QuestionnaireFilterEnum, QuestionnaireOrderEnum } from "@/generated/gql/graphql";
import { useTranslation } from "@/i18n/client";
import { useSessionContext } from "@/session";

export const Route = createFileRoute("/$organizationKey/$dossierId/questionnaires/")({
  component: Page,
});

function Page() {
  const { organizationKey, dossierId } = useSessionContext();
  const { data, refetch, networkStatus } = useQuery(GetAllQuestionnaires, {
    variables: {
      organizationKey,
      first: 10,
      orderBy: QuestionnaireOrderEnum.NameAsc,
      filterBy: QuestionnaireFilterEnum.All,
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const [searchTerms, setSearchTerms] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<QuestionnaireOrderEnum>(QuestionnaireOrderEnum.NameAsc);
  const [filterBy, setFilterBy] = useState<QuestionnaireFilterEnum>(QuestionnaireFilterEnum.All);
  const { t } = useTranslation("Questionnaires");

  const { handlePaginationChangePrevious, handlePaginationChangeNext } = useQuestionnairePagination(
    refetch,
    data,
  );

  useDebouncedSearch(searchTerms, refetch, organizationKey);

  const changeOrderBy = (order: QuestionnaireOrderEnum) => {
    setOrderBy(order);
    refetch({
      organizationKey,
      first: 10,
      orderBy: order,
      filterBy: filterBy,
      searchTerms: searchTerms,
    });
  };

  const changeFilterBy = (filter: QuestionnaireFilterEnum) => {
    setFilterBy(filter);
    refetch({
      organizationKey,
      first: 10,
      filterBy: filter,
      orderBy: orderBy,
      searchTerms: searchTerms,
    });
  };

  const changeSearchTerms = (value: string) => {
    setSearchTerms(value);
  };

  return (
    <>
      <Section variant="header">
        <div className="flex items-center gap-2">
          <header className="flex-1 text-2xl">{t("header")}</header>
        </div>

        <div className="space-y-4">
          <div className="flex">
            <CommandPrimitive className="flex h-full w-full flex-1 flex-col">
              <div className="bg-popover text-popover-foreground w-full flex-1 rounded-md">
                <div className="flex items-center px-3">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <CommandPrimitive.Input
                    className="placeholder:text-muted-foreground flex h-12 w-full rounded-md border-b-0 bg-transparent py-3 text-lg outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    autoFocus
                    placeholder={t("searchPlaceholder")}
                    value={searchTerms ?? ""}
                    onValueChange={changeSearchTerms}
                  />
                </div>
              </div>
            </CommandPrimitive>
          </div>
        </div>
      </Section>
      <Section variant="content">
        <div className="flex">
          <div className="mr-4 flex basis-4/5 flex-row justify-between gap-4">
            <Filters
              orderBy={orderBy}
              filterBy={filterBy}
              changeOrderBy={changeOrderBy}
              changeFilterBy={changeFilterBy}
            />
          </div>
          {data && (
            <Pagination
              data={data}
              handlePaginationChangePrevious={handlePaginationChangePrevious}
              handlePaginationChangeNext={handlePaginationChangeNext}
            />
          )}
        </div>

        {!data && (
          <DataBlock>
            <SkeletonLoader />
          </DataBlock>
        )}

        {data && (
          <>
            <QuestionnaireItemList
              data={data}
              networkStatus={networkStatus}
              organizationKey={organizationKey}
              dossierId={dossierId}
            />
          </>
        )}

        {data && (
          <Pagination
            data={data}
            handlePaginationChangePrevious={handlePaginationChangePrevious}
            handlePaginationChangeNext={handlePaginationChangeNext}
          />
        )}
      </Section>
    </>
  );
}
